import { inject } from '@angular/core';
import { ResolveFn, Routes } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { AppLayoutComponent } from './layout/app.layout.component';
import { CompanyComponent } from './rgs/components/admin/components/company/company/company.component';
import { RoleAccess } from './rgs/components/company-data/role/models/role-access.model';
import { EnvironmentSelectorComponent } from './rgs/components/environment-selector/environment-selector.component';
import { kibRoutes } from './rgs/components/kib/kib.routes';
import { AdminGuard } from './shared/guards/admin.guard';
import { canActivateAuthRole } from './shared/guards/app-auth.guard';
import { MaintenanceGuard } from './shared/guards/maitenance.guard';
import { RoleAccessGuard } from './shared/guards/role-access.guard';
import { UserGuard } from './shared/guards/user.guard';

const companyProfileResolver: ResolveFn<string> = () => {
  const translateSrv = inject(TranslateService);
  return translateSrv.get('sidebar.companyProfile').pipe(map((res: string) => `RGS+ | ${res}`));
};

const inspectionsResolver: ResolveFn<string> = () => {
  const translateSrv = inject(TranslateService);
  return translateSrv.get('collections.inspections.plural').pipe(map((res: string) => `RGS+ | ${res}`));
};

const scenariosResolver: ResolveFn<string> = () => {
  const translateSrv = inject(TranslateService);
  return translateSrv.get('collections.scenarios.plural').pipe(map((res: string) => `RGS+ | ${res}`));
};

export const routes: Routes = [
  {
    path: 'maitenance',
    loadChildren: () => import('./rgs/components/maitenance/maitenance.routes').then((m) => m.MAITENANCE_ROUTES),
  },
  {
    path: '',
    redirectTo: '/objects',
    pathMatch: 'full',
  },
  {
    path: 'downloaded/:id/:type/:format',
    loadComponent: () => import('./rgs/components/downloaded/downloaded.component').then((m) => m.DownloadedComponent),
  },
  {
    canActivate: [canActivateAuthRole, MaintenanceGuard],
    path: '',
    component: AppLayoutComponent,
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./rgs/components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [UserGuard],
      },
      {
        path: 'data-analysis',
        loadChildren: () =>
          import('./rgs/components/data-analysis/data-analysis.routes').then((m) => m.DATA_ANALYSIS_ROUTES),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { module: 'dataAnalysis', access: RoleAccess.READ },
      },
      {
        path: 'inspections',
        loadComponent: () =>
          import('./rgs/components/project-data/inspections/inspections.component').then((m) => m.InspectionsComponent),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { module: 'inspections', access: RoleAccess.READ, breadcrumb: 'collections.inspections.plural' },
        title: inspectionsResolver,
      },
      {
        path: 'scenarios',
        loadComponent: () =>
          import('./rgs/components/project-data/scenarios/scenarios.component').then((m) => m.ScenariosComponent),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { module: 'scenarios', access: RoleAccess.READ, breadcrumb: 'collections.scenarios.plural' },
        title: scenariosResolver,
      },
      {
        path: 'tasks',
        loadComponent: () =>
          import('./rgs/components/project-data/task-list/task-list.component').then((m) => m.TaskListComponent),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { module: 'tasks', access: RoleAccess.READ },
        children: [
          {
            path: 'dialog',
            children: [
              {
                path: ':mode',
                loadComponent: () =>
                  import('./rgs/components/objects/object-item/object-strategy/components/task-dialog/task-dialog.component').then(
                    (m) => m.TaskDialogComponent,
                  ),
              },
              {
                path: ':mode/:id',
                loadComponent: () =>
                  import('./rgs/components/objects/object-item/object-strategy/components/task-dialog/task-dialog.component').then(
                    (m) => m.TaskDialogComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'master',
        loadChildren: () => import('./rgs/components/masterdata/masterdata.routes').then((m) => m.MASTERDATA_ROUTES),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { module: 'masterdata', access: RoleAccess.READ, context: 'masterdata' },
      },
      {
        path: 'objects',
        loadChildren: () => import('./rgs/components/objects/object.routes').then((m) => m.OBJECT_ROUTES),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { module: 'objects', access: RoleAccess.READ },
      },
      {
        path: 'companyProfile',
        component: CompanyComponent,
        title: companyProfileResolver,
        canActivate: [UserGuard, RoleAccessGuard],
        data: { breadcrumb: 'sidebar.companyProfile', module: 'companyProfile', access: RoleAccess.READ },
      },
      {
        path: 'user-profile',
        data: { breadcrumb: 'topbar.profile' },
        loadChildren: () => import('./rgs/components/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
      },
      {
        path: 'clients',
        loadChildren: () => import('./rgs/components/company-data/client/client.routes').then((m) => m.CLIENT_ROUTES),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { breadcrumb: 'sidebar.clients', module: 'clients', access: RoleAccess.READ },
      },
      {
        path: 'users',
        loadChildren: () => import('./rgs/components/company-data/user/user.routes').then((m) => m.USER_ROUTES),
        canActivate: [UserGuard, RoleAccessGuard],
        data: { breadcrumb: 'collections.users.plural', module: 'users', access: RoleAccess.READ },
      },
      {
        path: 'roles',
        loadChildren: () => import('./rgs/components/company-data/role/role.routes').then((m) => m.ROLE_ROUTES),
        canActivate: [UserGuard, RoleAccessGuard],
        data: {
          breadcrumb: 'collections.roles.plural',
          module: 'roles',
          access: RoleAccess.READ,
          context: 'masterdata',
        },
      },
      {
        path: 'admin',
        canActivate: [AdminGuard],
        loadChildren: () => import('./rgs/components/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
        data: { context: 'admin' },
      },
      ...kibRoutes,
    ],
  },
  {
    path: 'environment-selector',
    component: EnvironmentSelectorComponent,
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'notfound',
    loadChildren: () => import('./rgs/components/notfound/notfound.routes').then((m) => m.NOTFOUND_ROUTES),
  },
  { path: '**', redirectTo: '/notfound' },
];
